import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DriversTable = ({ drivers, onSelectDriver, selectedDriverId, onEdit, onDelete, onBulkAction }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'driverId', direction: 'asc' });

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedRows(drivers?.map(d => d?.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getAssignmentBadge = (type) => {
    const badges = {
      'Primary': { icon: 'Star', color: 'text-primary bg-primary/10' },
      'Relief': { icon: 'Users', color: 'text-secondary bg-secondary/10' },
      'Unassigned': { icon: 'AlertCircle', color: 'text-muted-foreground bg-muted/50' }
    };
    return badges?.[type] || badges?.['Unassigned'];
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 h-full flex flex-col">
      <div className="sticky top-0 bg-card border-b border-border px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-semibold text-foreground">
              Drivers ({drivers?.length})
            </h3>
            {selectedRows?.length > 0 && (
              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-md">
                {selectedRows?.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {selectedRows?.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="UserCheck"
                  onClick={() => onBulkAction('activate', selectedRows)}
                >
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="UserX"
                  onClick={() => onBulkAction('deactivate', selectedRows)}
                >
                  Deactivate
                </Button>
              </>
            )}
            <Button
              variant="default"
              size="sm"
              iconName="UserPlus"
              onClick={() => onEdit(null)}
            >
              Add Driver
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0 z-[5]">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.length === drivers?.length && drivers?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('driverId')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Driver ID</span>
                  <Icon name="ArrowUpDown" size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Driver Name</span>
                  <Icon name="ArrowUpDown" size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Assignment</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Yard Location</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {drivers?.map((driver) => {
              const assignmentBadge = getAssignmentBadge(driver?.assignmentType);
              return (
                <tr
                  key={driver?.id}
                  onClick={() => onSelectDriver(driver)}
                  className={`hover:bg-muted/30 transition-hover cursor-pointer ${
                    selectedDriverId === driver?.id ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows?.includes(driver?.id)}
                      onChange={(e) => {
                        e?.stopPropagation();
                        handleSelectRow(driver?.id);
                      }}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-foreground">{driver?.driverId}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-foreground">{driver?.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground font-mono">{driver?.phone}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Icon name={assignmentBadge?.icon} size={14} className={assignmentBadge?.color} strokeWidth={2} />
                      <span className="text-sm text-foreground">{driver?.currentAssignment || 'None'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">{driver?.yardLocation}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${driver?.isActive ? 'bg-success' : 'bg-muted-foreground'}`}></div>
                      <span className="text-sm text-muted-foreground">{driver?.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-1">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onEdit(driver);
                        }}
                        className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-hover"
                        title="Edit driver"
                      >
                        <Icon name="Edit2" size={16} strokeWidth={2} />
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onDelete(driver);
                        }}
                        className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-hover"
                        title="Delete driver"
                      >
                        <Icon name="Trash2" size={16} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sticky bottom-0 bg-card border-t border-border px-4 py-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Showing {drivers?.length} drivers</span>
          <div className="flex items-center space-x-2">
            <span>Rows per page: 20</span>
            <div className="flex items-center space-x-1">
              <button className="p-1 hover:bg-muted/50 rounded transition-hover">
                <Icon name="ChevronLeft" size={16} strokeWidth={2} />
              </button>
              <span className="px-2">1 of 1</span>
              <button className="p-1 hover:bg-muted/50 rounded transition-hover">
                <Icon name="ChevronRight" size={16} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriversTable;